/* External */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';

/* Ott-common */
import {
  Errors, Npaw, createGetPlayerSchedules, createSchedulesCache,
} from '@dtvgo/player-utils';
import { LiveInfoProvider, useHandlePlayerError, useLivePlayerSchedules } from '@dtvgo/player-utils-react';
import { useFlag } from '@dtvgo/react-utils';

/* Components */
import VrioBasePlayer from './VrioBasePlayer';
import PlayerUI from '../PlayerUI';
import NpawMonitoringContainer from '../NpawMonitoring.container';
import PlayerGtmTrack from '../PlayerGtmTrack';
import ErrorScreen from '../errorScreen/ErrorScreen';

/* Other */
import { api } from '../../state/configureStore';
import { backendService } from '../../services';
import { usePlaybackHeartbeatController, useLoadOnChromecastConnect, useParentalControlError } from '../utils/hooks';
import {
  NPAW_PLATFORM, PLAYER_FAKE_SCHEDULE_DEFAULT_DURATION_MINUTES, PLAYER_FETCH_SCHEDULES_RETRY_COUNT,
  PLAYER_FETCH_SCHEDULES_RETRY_SECONDS,
  PLAYER_LONG_PAUSE_GO_TO_LIVE_DURATION_MINUTES,
} from '../../utils';
import { parentalControlRedirect } from '../utils';
import { isProgramCompletionActive } from '../../utils/env';

const CACHE_SIZE = 4;
const IS_DELAYED_WINDOW_SECONDS = 10;
const getPlayerSchedules = createGetPlayerSchedules(backendService, {
  pageSize: CACHE_SIZE,
  maxAttempts: PLAYER_FETCH_SCHEDULES_RETRY_COUNT + 1,
  delayBetweenAttempts: PLAYER_FETCH_SCHEDULES_RETRY_SECONDS * 1000,
});
const schedulesCache = createSchedulesCache(getPlayerSchedules);

function VrioLivePlayer({
  match: {
    params: { channelId: currentChannelId },
  },
}) {
  const {
    streamedTimeCounter,
    streamedContentTimeCounter,
    playbackHeartbeatController,
  } = usePlaybackHeartbeatController();
  const [authorizerResponse, setAuthorizerResponse] = useState({});
  const [hasCompletedProgram, setHasCompletedProgram] = useState(false);
  const [showPlayButton, { enable: displayPlayButton, disable: hidePlayButton }] = useFlag(false);
  const [readyToPlay, { enable: handleReadyToPlay }] = useFlag(false);
  const gtmRef = useRef();
  const uiRef = useRef();
  const pauseTime = useRef();

  const [
    { schedules, isLoading: isLoadingSchedules },
    { handleProgress: onProgress, updateState },
  ] = useLivePlayerSchedules(
    currentChannelId,
    schedulesCache,
    PLAYER_FAKE_SCHEDULE_DEFAULT_DURATION_MINUTES,
    IS_DELAYED_WINDOW_SECONDS,
  );

  const { channel } = api.useGetChannelsQuery(
    { assetToken: false },
    {
      selectFromResult: ({ data, ...result }) => ({
        channel: data?.channels?.find(({ channelId }) => channelId === currentChannelId),
        ...result,
      }),
    },
  );

  const {
    error, reportableError, handleError, handleReportableError,
  } = useHandlePlayerError();
  const npawError = useMemo(
    () => Npaw.errorParser(NPAW_PLATFORM, reportableError),
    [reportableError],
  );

  const currentSchedule = schedules.current;

  const handleProgramCompletion = useCallback(async () => {
    if (isProgramCompletionActive) {
      await playbackHeartbeatController?.programCompletion().catch((programCompletionError) => {
        console.error('[VrioLivePlayer] handle programCompletion > error in programCompletion', programCompletionError);
      });
    }
  }, [playbackHeartbeatController]);

  const handleProgress = useCallback((timeShift) => {
    const currentTime = Date.now();
    const delayInMilliseconds = Math.abs(timeShift) * 1000;
    const playerTime = currentTime - delayInMilliseconds;
    const endTime = new Date(currentSchedule?.live?.endTime).getTime();

    if (endTime && playerTime > endTime && !hasCompletedProgram) {
      handleProgramCompletion();
      setHasCompletedProgram(true);
      streamedContentTimeCounter?.reset();
      streamedContentTimeCounter?.start();
    }

    onProgress(playerTime, currentTime);
  }, [
    currentSchedule?.live?.endTime,
    streamedContentTimeCounter,
    hasCompletedProgram,
    handleProgramCompletion,
    onProgress,
  ]);

  useEffect(() => {
    if (currentSchedule?.live?.endTime) {
      setHasCompletedProgram(false);
    }
  }, [currentSchedule?.live?.endTime]);

  const handleGoLive = useCallback(() => {
    updateState({ currentTimestamp: Date.now(), useNowNext: true, hardUpdate: true });
  }, [updateState]);

  const handleBeforePlay = useCallback(() => {
    if (
      Date.now() - pauseTime.current
      >= PLAYER_LONG_PAUSE_GO_TO_LIVE_DURATION_MINUTES * 60 * 1000
    ) {
      uiRef.current?.goToLive();
    }
  }, []);

  const handlePause = useCallback(() => {
    pauseTime.current = Date.now();
  }, []);

  const handlePlaying = useCallback(() => {
    pauseTime.current = undefined;
  }, []);

  useEffect(() => {
    if (currentSchedule?.live?.blackout) {
      handleReportableError(Errors.getBlackoutError(currentSchedule));
    }
  }, [currentSchedule, handleReportableError]);

  useParentalControlError(currentSchedule, handleReportableError);

  const vmsId = currentSchedule?.vmsId ?? channel?.vmsId ?? '';
  const type = currentSchedule?.type ?? channel?.type ?? '';

  useLoadOnChromecastConnect(currentChannelId, type, vmsId, '', true);

  if (error) {
    if (error.category === Errors.ErrorCategory.PARENTAL) {
      parentalControlRedirect(currentSchedule);
      return null;
    }
    return (
      <ErrorScreen isLive asset={currentSchedule} error={error} />
    );
  }

  return (
    <VrioBasePlayer
      isLive
      playbackId={currentChannelId}
      readyToInit={!!currentSchedule && !isLoadingSchedules}
      asset={currentSchedule}
      onReadyToPlay={handleReadyToPlay}
      onProgress={handleProgress}
      onPlay={hidePlayButton}
      onPause={handlePause}
      onPlaying={handlePlaying}
      onAutoplayFailed={displayPlayButton}
      onError={handleError}
      onAuthorizerResponseReceived={setAuthorizerResponse}
      onAuthorizerError={handleReportableError}
      playbackHeartbeatController={playbackHeartbeatController}
      streamedTimeCounter={streamedTimeCounter}
      streamedContentTimeCounter={streamedContentTimeCounter}
    >
      <NpawMonitoringContainer
        isLive
        asset={currentSchedule}
        channelId={authorizerResponse.channelId}
        channelName={channel?.title}
        provider={authorizerResponse.provider}
        error={npawError}
      />
      <LiveInfoProvider schedule={currentSchedule?.live}>
        {readyToPlay && <PlayerGtmTrack isLive asset={currentSchedule} ref={gtmRef} />}
        <PlayerUI
          isLive
          asset={currentSchedule}
          channelLogo={channel?.images}
          channelName={channel?.title}
          readyToPlay={readyToPlay}
          nextAsset={schedules.next}
          displayAreYouStillWatching
          showPlayButton={showPlayButton}
          gtmInteraction={gtmRef.current?.gtmInteraction}
          onGoLive={handleGoLive}
          onBeforePlay={handleBeforePlay}
          ref={uiRef}
        />
      </LiveInfoProvider>
    </VrioBasePlayer>
  );
}

VrioLivePlayer.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      channelId: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export default VrioLivePlayer;
